export type BotCustomParams = Record<string, string | number | boolean>;

// Cap on the encoded customParams placed in the bot iframe URL, so an oversized identify
// object can't produce a URL beyond typical browser/server limits (~8k).
export const MAX_CUSTOM_PARAMS_URL_LEN = 6000;

// Builds the "&customParams=<encoded-json>" URL fragment from identify params, or '' when
// there are none / the payload is too large (warns so it is never a silent drop). Shared by
// the bot-widget loader (init, swapBot, iframe embed).
export const buildCustomParamsQuery = (
  customParams: BotCustomParams | null | undefined,
  maxLen = MAX_CUSTOM_PARAMS_URL_LEN
): string => {
  if (!customParams || Object.keys(customParams).length === 0) return '';
  const encoded = encodeURIComponent(JSON.stringify(customParams));
  if (encoded.length > maxLen) {
    // eslint-disable-next-line no-console
    console.warn(
      `[GetDemo] customParams too large for the iframe URL (${encoded.length} chars) — ` +
        'skipping to avoid a broken URL. Use fewer/smaller identify fields.'
    );
    return '';
  }
  return `&customParams=${encoded}`;
};

export const parseCustomParamsFromUrl = (
  search = window.location.search
): BotCustomParams | undefined => {
  try {
    const raw = new URLSearchParams(search).get('customParams');
    if (!raw) return undefined;

    const parsed = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return undefined;
    }

    const result: BotCustomParams = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        result[key] = value;
      }
    }
    return Object.keys(result).length > 0 ? result : undefined;
  } catch {
    return undefined;
  }
};
